import { useEffect, useRef, useState } from 'react';

export default function useDriverPointerDrag(onAssign, scope) {
    const [preview, setPreview] = useState(null);
    const assignment = useRef(onAssign);
    assignment.current = onAssign;
    const cleanup = useRef(null);

    useEffect(() => {
        cleanup.current?.();
        setPreview(null);
        return () => cleanup.current?.();
    }, [scope]);

    const startDrag = (event, driver) => {
        if (event.button !== 0 || event.isPrimary === false) return;
        event.preventDefault();
        cleanup.current?.();
        const { pointerId, clientX: startX, clientY: startY } = event;
        let dragging = false;
        let target = null;

        const findTarget = (x, y) => {
            const row = document.elementFromPoint(x, y)?.closest('[data-driver-drop-route]') ?? null;
            if (target !== row) {
                target?.classList.remove('driver-drop-target');
                target = row;
                target?.classList.add('driver-drop-target');
            }
            return row;
        };
        const move = (pointerEvent) => {
            if (pointerEvent.pointerId !== pointerId) return;
            const { clientX: x, clientY: y } = pointerEvent;
            if (!dragging && Math.hypot(x - startX, y - startY) < 6) return;
            dragging = true;
            pointerEvent.preventDefault();
            findTarget(x, y);
            setPreview({ driver, x, y });
        };
        const cancel = () => {
            cleanup.current?.();
            setPreview(null);
        };
        const finish = (pointerEvent) => {
            if (pointerEvent.pointerId !== pointerId) return;
            const routeUuid = dragging
                ? findTarget(pointerEvent.clientX, pointerEvent.clientY)?.dataset.driverDropRoute
                : null;
            cancel();
            if (routeUuid) void assignment.current(driver, routeUuid);
        };
        const cancelPointer = (pointerEvent) => {
            if (pointerEvent.pointerId === pointerId) cancel();
        };
        const keyDown = (keyEvent) => {
            if (keyEvent.key === 'Escape') cancel();
        };

        document.addEventListener('pointermove', move, { passive: false });
        document.addEventListener('pointerup', finish);
        document.addEventListener('pointercancel', cancelPointer);
        document.addEventListener('keydown', keyDown);
        window.addEventListener('blur', cancel);
        cleanup.current = () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', finish);
            document.removeEventListener('pointercancel', cancelPointer);
            document.removeEventListener('keydown', keyDown);
            window.removeEventListener('blur', cancel);
            target?.classList.remove('driver-drop-target');
            cleanup.current = null;
        };
    };

    return { startDrag, preview };
}
